import { useState } from 'react';
import { Edit2, Trash2, Phone, MapPin, DollarSign, CreditCard, AlertTriangle } from 'lucide-react';
import useCustomerStore from '../../stores/useCustomerStore';

/**
 * Customer Card Component
 * عرض بطاقة عميل واحد مع الإجراءات
 *
 * Protocols Applied (From Father DB):
 * - P-COMPONENT-001: Component Structure
 * - P-UX-001: User Experience
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode
 * - P010: Error Handling
 * - CUST-002: Credit Limit Management
 * - CUST-004: Customer Balance Calculation
 */
const CustomerCard = ({ customer, onEdit }) => {
  const { deleteCustomer } = useCustomerStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Generate avatar initials
  const getInitials = (name) => {
    const parts = name.split(' ');
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : parts[0][0];
  };

  // Generate avatar color based on customer ID
  const avatarColors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-teal-500'
  ];
  const avatarColor = avatarColors[customer.id % avatarColors.length];

  // Map API fields (support both snake_case from API and camelCase from mock data)
  const creditLimit = customer.credit_limit ?? customer.creditLimit ?? 0;
  const currentBalance = customer.current_balance ?? customer.currentBalance ?? 0;
  const totalPurchases = customer.total_purchases ?? customer.totalPurchases ?? 0;
  const lastPurchaseDate = customer.last_purchase_date ?? customer.lastPurchaseDate ?? null;

  // Map status (1=active, 0=inactive)
  const isActive = customer.status === 1 || customer.status === '1' || customer.status === 'active';

  // Calculate credit usage percentage
  const creditUsage = creditLimit > 0
    ? (currentBalance / creditLimit) * 100
    : 0;

  // Determine credit status
  const getCreditStatus = () => {
    if (creditUsage > 100) {
      return {
        color: 'red',
        label: 'تجاوز الحد',
        bgClass: 'bg-red-500',
        textClass: 'text-red-700 dark:text-red-300'
      };
    } else if (creditUsage >= 80) {
      return {
        color: 'orange',
        label: 'قريب من الحد',
        bgClass: 'bg-orange-500',
        textClass: 'text-orange-700 dark:text-orange-300'
      };
    } else if (creditUsage >= 50) {
      return {
        color: 'yellow',
        label: 'متوسط',
        bgClass: 'bg-yellow-500',
        textClass: 'text-yellow-700 dark:text-yellow-300'
      };
    } else {
      return {
        color: 'green',
        label: 'آمن',
        bgClass: 'bg-green-500',
        textClass: 'text-green-700 dark:text-green-300'
      };
    }
  };

  const creditStatus = getCreditStatus();

  // Handle Delete
  const handleDelete = () => {
    try {
      deleteCustomer(customer.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700">
        {/* Card Header */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div
              className={`w-14 h-14 ${avatarColor} rounded-full flex items-center justify-center flex-shrink-0`}
            >
              <span className="text-white font-bold text-xl">
                {getInitials(customer.name)}
              </span>
            </div>

            {/* Customer Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white truncate">
                    {customer.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    كود: {customer.code}
                  </p>
                </div>

                {/* Badges Container */}
                <div className="flex flex-col gap-1">
                  {/* Customer Type Badge */}
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium text-center ${
                      customer.customer_type === 'credit' || creditLimit > 0
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {customer.customer_type === 'credit' || creditLimit > 0 ? 'آجل' : 'نقدي'}
                  </span>

                  {/* Status Badge */}
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium text-center ${
                      isActive
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {isActive ? 'نشط' : 'غير نشط'}
                  </span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span className="font-mono" dir="ltr">
                    {customer.phone}
                  </span>
                </div>
                {customer.address && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{customer.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Credit Limit Section */}
        <div className="px-4 pb-4">
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
            {/* Credit Info */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  الحد الائتماني
                </span>
              </div>
              <span className={`text-xs font-semibold ${creditStatus.textClass}`}>
                {creditStatus.label}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mb-2">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${creditStatus.bgClass} transition-all duration-300`}
                  style={{ width: `${Math.min(creditUsage, 100)}%` }}
                />
              </div>
            </div>

            {/* Balance vs Limit */}
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">الرصيد: </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {currentBalance.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">الحد: </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {creditLimit.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
                </span>
              </div>
            </div>

            {/* Usage Percentage */}
            <div className="mt-1 text-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                استخدم {creditUsage.toFixed(1)}% من الحد
              </span>
            </div>

            {/* Over Limit Warning */}
            {creditUsage > 100 && (
              <div className="mt-2 flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-2 py-1.5">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                <span className="text-xs text-red-700 dark:text-red-300 font-medium">
                  تجاوز بمبلغ {(currentBalance - creditLimit).toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
              <p className="text-xs text-gray-600 dark:text-gray-400">إجمالي المشتريات</p>
              <p className="font-bold text-blue-700 dark:text-blue-400">
                {totalPurchases.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-2">
              <p className="text-xs text-gray-600 dark:text-gray-400">آخر شراء</p>
              <p className="font-bold text-purple-700 dark:text-purple-400 text-sm">
                {lastPurchaseDate
                  ? new Date(lastPurchaseDate).toLocaleDateString('ar-KW')
                  : 'لا يوجد'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-end gap-2">
          <button
            onClick={() => onEdit(customer)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            تعديل
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            حذف
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                  حذف العميل
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  هل أنت متأكد؟
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-6">
              سيتم حذف العميل <span className="font-bold">"{customer.name}"</span> نهائياً.
              هذا الإجراء لا يمكن التراجع عنه.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition-colors"
              >
                نعم، احذف
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white py-2 rounded-lg font-medium transition-colors"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerCard;
